import { Component, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, ComponentFactoryResolver, DoCheck } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreditCardType, GeographicalRegion, Country, Member, QueryParamService, OvsCurrencyPipe, Core } from '../shared/core/ovs.core';
import { ResortImageType, ResortGuest, ResortApplicationConfiguration, BookingResult, ResortInventory, } from '../models/presentation.model';
import { DisplayValue } from '../models/browser.model';
import { ResortComponent } from '../shared/resort.component';
import { ResortBookingClientBase } from '../services/resort-booking.client';
import { LinguaBase } from '../shared/core/ovs.lingua';
import { TermsComponent } from './terms.component';
import { MatDialog, MatDialogRef } from '@angular/material';
import * as moment from 'moment';
import { CreditCardService } from '../services/creditcard.service';
import { CardTypes } from '../models/card-types.enum';
import { ResortInformation } from './resort-information/resort-information.model';
import { ResortDetailClientBase } from '../services/resort-detail.client';
import { Animations } from '../shared/animations/animations';
import { CheckoutPageEvents, ResortPageViews } from '../instrumentation/resort-custom-events';
import { EncoreService } from '../services/encore.service';
import { ModalConfirmComponent } from '../shared/modal-confirm/modal-confirm.component';
import { PricingInformationComponent } from './pricing-information/pricing-information.component';
import { Router } from '@angular/router';
import { UserActivityLogger, UserActivityLoggerOptions, UserActivityPublishDataTypes } from '../instrumentation/user-activity-logger.service';
import { CookieService, CookieOptions } from '@ngx-toolkit/cookie';
import { DomainService } from '../services/domain.service';
import { ResortApplication } from '../services/resort-application.service';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { MessageType } from '../shared/alert/alert.component';

@Component({
    selector: 'check-out',
    templateUrl: './check-out.component.html',
    styleUrls: ['./check-out.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.slideInOut],
    providers: [CreditCardService, ResortApplication]
})
export class CheckoutComponent extends ResortComponent implements AfterViewInit, DoCheck {
    private _bookingService: ResortBookingClientBase;
    private _resortClient: ResortDetailClientBase;
    private _lingua: LinguaBase;
    private _dialog: MatDialog;
    private _termsDialog: MatDialogRef<TermsComponent>;
    private _instantConfirm: boolean = false;

    public checkoutForm: FormGroup;

    public selectedCardType: CreditCardType;
    public states: GeographicalRegion[] = [];
    public months: DisplayValue<number>[];
    public years: DisplayValue<number>[];
    public members: Member[] = [];
    public countries: Country[] = [];
    public hasAcceptedTerms: boolean = false;
    public isCompleting: boolean = false;
    public isBookingCompleted: boolean = false;
    public grandTotal: number;
    public resortInformation: ResortInformation = new ResortInformation();
    public autoCreditCardType: CardTypes = CardTypes.Unknown;
    public updateTotal: number = 0;
    public allInclusiveGuestsQty: number = 2;
    public isFormInvalid: boolean = false;
    public invStorageKey: string = "ovs.chkdata.inventory";
    public resortStorageKey: string = "ovs.chkdata.resort";
    public showHideEdit: boolean;
    public paymentDeclined: boolean = false;
    public isStartBookingCompleted: boolean = false;
    public isInventoryLoaded: boolean = false;
    public invalidFormMessageType: MessageType = MessageType.DANGER;

    private sorWeeksType: number = 5;
    private checkOutPageViewLogged: boolean = false;
    private doCheckOutPageView: boolean = false;

    guestCounterIndex = 0;
    components: any[] = new Array();
    $disclaimer: Observable<string>;

    public get creditCardImageName(): string {
        return CardTypes[this.autoCreditCardType];
    }

    public get creditCardImageUrl(): string {
        return `url('/assets/credit-cards/${this.creditCardImageName}.png')`;
    }

    public get hasEarnRewards(): boolean {
        return this.application.currentInventory.reward > 0;
    }

    public get inventory(): ResortInventory {
        return this.application.currentInventory;
    }

    public get childOccupancy(): number {
        return this.application.currentInventory.childOccupancy
    }

    public get upgradeTitle(): string {
        return (<ResortApplicationConfiguration>this.application.configuration).UpgradeLabels.CheckoutDialogTitle
            .replaceStr('{{upgrade.vc}}',
                (this.application.currentInventory.rewardWithUpgrade - this.application.currentInventory.reward)
                    .toString());
    }

    public get upgradeMessage(): string {
        const pipe = new OvsCurrencyPipe(Core.current);

        if (this.application.currentBooking && this.application.currentBooking.UpgradeFee) {
            return (<ResortApplicationConfiguration>this.application.configuration).UpgradeLabels.CheckoutDialogMessage
                .replaceStr('{{upgrade.price}}', pipe.transform(this.application.currentBooking.UpgradeFee));
        } else {
            return (<ResortApplicationConfiguration>this.application.configuration).UpgradeLabels.CheckoutDialogMessage;
        }
    }

    public get upgradeYesText(): string {
        return (<ResortApplicationConfiguration>this.application.configuration).UpgradeLabels.CheckoutDialogYesText;
    }

    public get upgradeNoText(): string {
        return (<ResortApplicationConfiguration>this.application.configuration).UpgradeLabels.CheckoutDialogNoText;
    }

    get guestFormList(): FormArray {
        return this.checkoutForm.get('guests') as FormArray;
    }

    get creditCardName(): AbstractControl {
        return this.checkoutForm.get('creditCardName');
    }

    get address1(): AbstractControl {
        return this.checkoutForm.get('address1');
    }

    get city(): AbstractControl {
        return this.checkoutForm.get('city');
    }

    get statedd(): AbstractControl {
        return this.checkoutForm.get('statedd');
    }

    get stateentry(): AbstractControl {
        return this.checkoutForm.get('stateentry');
    }

    get country(): AbstractControl {
        return this.checkoutForm.get('country');
    }

    get postalCode(): AbstractControl {
        return this.checkoutForm.get('postalCode');
    }

    get creditCardNumber(): AbstractControl {
        return this.checkoutForm.get('creditCardNumber');
    }

    get creditCardMonth(): AbstractControl {
        return this.checkoutForm.get('creditCardMonth');
    }

    get creditCardYear(): AbstractControl {
        return this.checkoutForm.get('creditCardYear');
    }

    get cvv(): AbstractControl {
        return this.checkoutForm.get('cvv');
    }

    private _guests: ResortGuest[] = [];
    get guests(): ResortGuest[] {

        return this._guests;
        
    };

    @ViewChild('creditCardNumberRef') creditCardNumberRef: ElementRef;
    @ViewChild('cancellationWaiverModal') cancellationWaiverModal: ModalConfirmComponent;
    @ViewChild('pricingInfoComponent') pricingInfoComponent: PricingInformationComponent;


    constructor(
        bookingService: ResortBookingClientBase,
        private router: Router,
        resortClient: ResortDetailClientBase,
        lingua: LinguaBase,
        dialog: MatDialog,
        private creditCardService: CreditCardService,
        private qryParamsService: QueryParamService,
        private cookieService: CookieService,
        private domainService: DomainService,
        private componentFactoryResolver: ComponentFactoryResolver,
        private resortApplication: ResortApplication,
        private formBuilder: FormBuilder
    ) {
        super();
        this._bookingService = bookingService;
        this._resortClient = resortClient;
        this._lingua = lingua;
        this._dialog = dialog;

        this.scrollToTop();

        this.$disclaimer = this.resortApplication.getDisclaimer().pipe(
            map(data => data.disclaimer)
        )

        this.initBookingForm();
    }

    handleErr(e) {
        if (!this.isBookingCompleted) {
            this.mapApplicationInsightsObjects(e, CheckoutPageEvents.BookingFailure);
        } else {
            this.mapApplicationInsightsObjects(e, CheckoutPageEvents.CheckoutPageError);
        }
    }

    public initialize(): void {
        try {
            super.initialize();

            let months: DisplayValue<number>[] = [];
            let years: DisplayValue<number>[] = [];
            let members: Member[] = [];

            for (let index: number = 0; index < 12; index++) {
                months.add(new DisplayValue(index + 1, moment(new Date(1900, index)).format("MM")));
            }

            
            for (let index: number = new Date().getFullYear(); index < new Date().getFullYear() + 20; index++) {
                years.add(new DisplayValue(index, index.toString()));
            }
            
            this.months = months;
            this.years = years;

            members.add(new Member());
            members.addRange((<ResortApplicationConfiguration>this.configuration).AssociatedMembers);

            this.members = members;
            this.countries = (<ResortApplicationConfiguration>this.configuration).Countries;

            if (this.configuration.IsClientSideTempDataPersistanceEnabled) {
                if (!this.application.currentInventory) {
                    var resortQueryParams = this.qryParamsService.resortDetailParams;
                    var inventoryInfo = this.qryParamsService.checkoutParams;
                    var self = this;
                    this._resortClient.Get(resortQueryParams.resortId,
                        resortQueryParams.startDate,
                        v => {
                            this.application.initiatecheckOut(v,
                                v.resortInventoryList.where(i => i.intervalId == inventoryInfo.intervalId)
                                    .firstOrDefault());
                            this.onResortInventoryLoadCompleted();
                        });
                } else {
                    this.onResortInventoryLoadCompleted();
                }
            } else {
                this.onResortInventoryLoadCompleted();
            }

        } catch (ex) {
            this.mapApplicationInsightsObjects(ex, CheckoutPageEvents.InventoryLoadFailure);
        }
    }

    /**
     * Initialize booking form
     */
    private initBookingForm(): void {
        this.checkoutForm = this.formBuilder.group({
            creditCardName: ['', [
                Validators.required,
                Validators.pattern(/^[a-zA-Z ]+$/)
            ]],
            address1: ['', Validators.required],
            address2: [''],
            city: ['', Validators.required],
            country: ['', Validators.required],
            statedd: [''],
            stateentry: [''],
            comments: [''],
            creditCardNumber: ['', [
                Validators.required,
                Validators.maxLength(16),
                Validators.pattern(/^[0-9]+$/)
            ]],
            creditCardMonth: ['', Validators.required],
            creditCardYear: ['', Validators.required],
            cvv: ['', [
                Validators.required,
                Validators.maxLength(4)
            ]],
            postalCode: ['', Validators.required],
            guests: this.formBuilder.array([])
        });
    }

    /**
     * Fill booking form with initial data
     */
    private fillBookingForm() {
        const RegionId = this.application.currentBooking.Address.RegionId;
        const bookingData = {
            creditCardName: this.application.currentBooking.CreditCardName,
            address1: this.application.currentBooking.Address.Address1,
            address2: this.application.currentBooking.Address.Address2,
            city: this.application.currentBooking.Address.City,
            statedd: this.application.currentBooking.Address.RegionId,
            stateentry: this.application.currentBooking.Address.Region,
            country: this.application.currentBooking.Address.CountryId,
            postalCode: this.application.currentBooking.Address.PostalCode,
            creditCardNumber: this.application.currentBooking.CreditCardNumber,
            creditCardMonth: this.application.currentBooking.CreditCardExpirationMonth,
            creditCardYear: this.application.currentBooking.CreditCardExpirationYear,
            cvv: this.application.currentBooking.CreditCardVerification,
            comments: this.application.currentBooking.Comments,
        };

        this.checkoutForm.patchValue(bookingData);

        this.checkoutForm.get('creditCardNumber').valueChanges.subscribe(value => {
            this.checkCreditCardType(value);
        });

        this.checkoutForm.get('country').valueChanges.subscribe(value => {
            this.onCountryChanged();
        });

        this.checkoutForm.get('statedd').valueChanges.subscribe(value => {
            this.onStateChanged();
        });

        this.onCountryChanged();

        this.application.currentBooking.Address.RegionId = RegionId;
        this.checkoutForm.get('statedd').setValue(RegionId);
    }

    private onResortInventoryLoadCompleted(): void {
        this.application.currentBooking.Address.CountryId = 1;

        this.application.currentBooking.IntervalId = this.application.currentInventory.intervalId;
        this.application.currentBooking.ResortId = this.application.currentResort.id;
        this.application.currentBooking.InventoryId = this.application.currentInventory.id;
        this.application.currentBooking.ResortInventory = this.application.currentInventory;
        this.application.currentBooking.CheckInDate = new Date(this.application.currentInventory.checkInDate.getTime() -
            60000 * this.application.currentInventory.checkInDate.getTimezoneOffset());
        this.application.currentBooking.CheckOutDate =
            new Date(this.application.currentInventory.checkOutDate.getTime() -
                60000 * this.application.currentInventory.checkOutDate.getTimezoneOffset());
        this.application.currentBooking.IsUpgradeAvailable = this.application.currentInventory.isUpgradeAvailable;

        this.resortInformation.inventory = this.application.currentInventory;
        this.resortInformation.detail = this.application.currentResort;
        this.resortInformation.propertyImageUrl = this.getResortImageUrl(ResortImageType.Property);
        this.resortInformation.unitImageUrl = this.getResortImageUrl(ResortImageType.UnitType);

        this.autoAssignPrimaryMember();

        var self = this;
        this._bookingService.Post(this.application.currentBooking, (r) => this.onStartBookingCompleted(r), self.handleErr.bind(this));

        UserActivityLogger.publish(this.application.currentInventory, UserActivityPublishDataTypes.Inventory);
        UserActivityLogger.publish(this.application.currentBooking, UserActivityPublishDataTypes.Booking);
        UserActivityLogger.logPageView(ResortPageViews.CheckoutPage, UserActivityLoggerOptions.AppInsightsOnly);
    }

    ngAfterViewInit(): void {
        if (this.application.configuration.IsAgent) {
            EncoreService.initForAgent(this.application.configuration.AgentJwt);
        }
    }

    /**
     * This approach is to avoid ExpressionChangedAfterItHasBeenCheckedError error
     */
    ngDoCheck(): void {
        const guestList: ResortGuest[] = [];

        this.guestFormList.controls.forEach((guestForm: FormGroup) => {
            const resortGuest = Object.assign(new ResortGuest(), guestForm.value);
            Object.setPrototypeOf(resortGuest, ResortGuest.prototype);
            guestList.push(resortGuest);
        });
        this._guests = guestList;
    }

    private onStartBookingCompleted(result: BookingResult): void {
        this.application.currentBooking.HasInsurance = result.insuranceChosen &&
            this.application.currentBooking.ResortInventory.inventoryType !== this.sorWeeksType;
        this.application.currentBooking.UpgradeFee = result.upgradeFee;
        this.updateTotal++;
        this.application.currentInventory.insurance =
            this.application.currentBooking.HasInsurance ? result.insurance : 0;
        this.isStartBookingCompleted = true;

        this.fillBookingForm();
    }

    public showTerms(): void {
        UserActivityLogger.logEvent(CheckoutPageEvents.ResortTermsLinkClicked,
            {},
            UserActivityLoggerOptions.AppInsightsOnly);

        this._termsDialog = this._dialog.open(TermsComponent);
    }

    protected getResortImageUrl(imageType: ResortImageType) {
        switch (imageType) {
            case ResortImageType.Property:
                return this.application.getImageUrl(this.getImagesByType(this.application.currentResort.resortImageList,
                    [ResortImageType.Property])[0]);
            case ResortImageType.UnitType:
                return this.application.getImageUrl(
                    this.getImagesByType(this.application.currentInventory.unitTypeImageList,
                        [ResortImageType.UnitType])[0]);
            default:
                return undefined;
        }
    }

    private autoAssignPrimaryMember(): void {
        this.autoSelectMember(this.application.configuration.Member);
    }

    private autoSelectMember(member: Member): void {
        if (member) {
            const selectedPurchaser = this.application.configuration.AssociatedMembers
                .where(m => m.FirstName == member.FirstName && m.LastName == member.LastName).firstOrDefault();

            this.onMemberChanged(selectedPurchaser);
        }
    }

    public onMemberChanged(member: Member): void {
        if (member) {
            this.application.currentBooking.EmailAddress = member.EmailAddress ? member.EmailAddress.Address : "";
            this.application.currentBooking.FirstName = member.FirstName;
            this.application.currentBooking.LastName = member.LastName;
            this.application.currentBooking.CreditCardName = member.FirstName + " " + member.LastName;
            this.application.currentBooking.PhoneNumber = member.PhoneNumbers && member.PhoneNumbers.length > 0
                ? member.PhoneNumbers.first().Number
                : "";
            if (this.application.currentBooking.Address.Address1 == "" ||
                this.application.currentBooking.Address.Address1 == null ||
                this.application.currentBooking.Address.Address1 == undefined) {
                this.application.currentBooking.Address.Address1 = member.Address ? member.Address.Address1 : "";
                this.application.currentBooking.Address.Address2 = member.Address ? member.Address.Address2 : "";
                this.application.currentBooking.Address.City = member.Address ? member.Address.City : "";
                this.application.currentBooking.Address.CountryId = member.Address ? member.Address.CountryId : 0;
                this.onCountryChanged();
                this.application.currentBooking.Address.RegionId = member.Address ? member.Address.RegionId : 0;
                this.application.currentBooking.Address.RegionCode = member.Address ? member.Address.RegionCode : "";
                this.application.currentBooking.Address.PostalCode = member.Address ? member.Address.PostalCode : "";
            }
        } else {
            this.showHideEdit = true;
        }
    }

    public onGuestMemberChanged(guest: ResortGuest, member: Member): void {
        guest.FirstName = member.FirstName;
        guest.LastName = member.LastName;
    }

    public onCountryChanged(): void {
        let states: GeographicalRegion[] = [];
        const countryId = this.checkoutForm.get('country').value;

        if (countryId > 0) {
            states = this.application.configuration.Regions.where(s => s.CountryId == countryId).toArray();
            this.application.currentBooking.Address.RegionId = undefined;
            this.application.currentBooking.Address.RegionCode = '';
            this.application.currentBooking.Address.Region = '';

            this.checkoutForm.get('statedd').setValue('');
            this.checkoutForm.get('stateentry').setValue('');
        }

        this.states = states;
        
        
        if (this.states.length > 0) {
            this.statedd.setValidators([Validators.required]);
            this.stateentry.clearValidators();
        } else {
            this.stateentry.setValidators([Validators.required]);
            this.statedd.clearValidators();
        }

        this.statedd.updateValueAndValidity();
        this.stateentry.updateValueAndValidity();
    }

    public onStateChanged(): void {
        const regionId = this.checkoutForm.get('statedd').value;

        if (this.states.where(s => s.Id === regionId).count() === 1) {
            const regionCode = this.states.where(s => s.Id === regionId).first().Code;

            this.application.currentBooking.Address.RegionCode = regionCode;
        }
    }

    public onGrandTotalChanged(total: number) {
        this.grandTotal = total;

        let inventory = this.application.currentInventory;
        let booking = this.application.currentBooking;

        let publishedData = UserActivityLogger.getPublishedData();
        if (publishedData !== undefined &&
            publishedData !== null &&
            publishedData.booking !== undefined &&
            publishedData.booking !== null &&
            publishedData.inventory !== undefined &&
            publishedData.inventory !== null) {
            try {
                let result = 0;
                result = (publishedData.inventory.priceWithTaxesAndFees -
                    publishedData.inventory.taxesAndFees +
                    publishedData.inventory.insurance -
                    publishedData.booking.Discount +
                    publishedData.inventory.certificateRedemptionFee +
                    publishedData.booking.AllInclusiveFees);

                if (result >= 0) {
                    this.doCheckOutPageView = true;
                }
            } catch (logEx) {
                this.doCheckOutPageView = false;
            }
        }

        if (this.doCheckOutPageView &&
            this.checkOutPageViewLogged === false &&
            this.grandTotal !== undefined &&
            this.grandTotal > 0 &&
            this.guestFormList.length > 0) {

            UserActivityLogger.logPageView(ResortPageViews.CheckoutPage,
                UserActivityLoggerOptions.TealiumOnly,
                { grandTotal: this.grandTotal, guestsCount: this.guestFormList.length, orderSubtotal: this.grandTotal });
            this.checkOutPageViewLogged = true;
        }
    }

    public get isBillingAddressValid(): boolean {
        const address1 = this.application.currentBooking.Address.Address1;
        if (!address1) {
            this.showHideEdit = true;
            return false;
        }
        const city = this.application.currentBooking.Address.City;
        if (!city) {
            this.showHideEdit = true;
            return false;
        }
        const country = this.application.currentBooking.Address.CountryId;
        if (!country) {
            this.showHideEdit = true;
            return false;
        }
        if (this.states.length > 0) {
            if (!this.application.currentBooking.Address.RegionId || this.application.currentBooking.Address.RegionId === 0)
                this.showHideEdit = true;
            return false;
        } else {
            if (!this.application.currentBooking.Address.Region || this.application.currentBooking.Address.Region.trim() === '') {
                this.showHideEdit = true;
                return false;
            }
        }

        const postalCode = this.application.currentBooking.Address.PostalCode;
        if (!postalCode || this.application.currentBooking.Address.PostalCode.trim() === '') {
            this.showHideEdit = true;
            return false;
        }

        return true;
    }

    private markAsTouched(formGroup: FormGroup) {
        (<any>Object).values(formGroup.controls).forEach(control => {
            control.markAsTouched();

            if (control.controls) {
                this.markAsTouched(control);
            }
        });
    }

    public continue(instantConfirm: boolean): void {
        this._instantConfirm = instantConfirm;
        this.isFormInvalid = false;
        
        if (this.application.currentBooking.Address.RegionId === 0) {
            this.application.currentBooking.Address.RegionId = undefined;
        }

        // TODO:  if we decide to just prevent the user from moving forward without an additional message.  [disabled]="{criteria}"
        if (this.checkoutForm.invalid || !this.hasAcceptedTerms) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.markAsTouched(this.checkoutForm);
            this.isFormInvalid = true;

            return;
        }

        let inventory = this.application && this.application.currentBooking;
        let currentInventory = this.application && this.application.currentInventory;

        let isSorWeeks = ((inventory === null || typeof inventory === 'undefined')
            ? false
            : (inventory.ResortInventory === null
                ? false
                : inventory.ResortInventory.inventoryType === this.sorWeeksType));

        let showCancellationWaiver = (inventory !== null && typeof inventory !== 'undefined' && isSorWeeks === false)
            ? Core.current.calculateDiff(currentInventory.checkInDate)
            : false;

        if (!this.application.currentBooking.HasInsurance && showCancellationWaiver) {
            UserActivityLogger.logEvent(CheckoutPageEvents.CancellationWaiverDispalyed, {}, UserActivityLoggerOptions.AppInsightsOnly);
            this.cancellationWaiverModal.openConfirmModal();
        }
        else {
            this.completeBooking();
        }
    }

    cancellationWaiveUserSelection(value: boolean) {
        UserActivityLogger.logEvent(CheckoutPageEvents.CancellationWaiverUserChoice, { userchoice: value ? "Accepted" : "Declined" }, UserActivityLoggerOptions.AppInsightsOnly);

        if (value) {
            this.application.currentBooking.HasInsurance = true;
            this.pricingInfoComponent.onAcceptDeclineChanged(value);
        }
        this.cancellationWaiverModal.closeConfirmModal();
        this.completeBooking();
    }

    private completeBooking() {
        try {
            const guests: ResortGuest[] = this.guests;
            const formValue = this.checkoutForm.value;
            const mainGuest = formValue.guests[0];

            this.application.currentBooking.EmailAddress = mainGuest.EmailAddress;
            this.application.currentBooking.PhoneNumber = mainGuest.PhoneNumber;
            this.application.currentBooking.CreditCardName = formValue.creditCardName;
            this.application.currentBooking.Address.Address1 = formValue.address1;
            this.application.currentBooking.Address.Address2 = formValue.address2;
            this.application.currentBooking.Address.City = formValue.city;
            this.application.currentBooking.Address.RegionId = formValue.statedd;
            this.application.currentBooking.Address.Region = formValue.stateentry;
            this.application.currentBooking.Address.CountryId = formValue.country;
            this.application.currentBooking.Address.PostalCode = formValue.postalCode;
            this.application.currentBooking.CreditCardNumber = formValue.creditCardNumber;
            this.application.currentBooking.CreditCardExpirationMonth = formValue.creditCardMonth;
            this.application.currentBooking.CreditCardExpirationYear = formValue.creditCardYear;
            this.application.currentBooking.CreditCardVerification = formValue.cvv;
            this.application.currentBooking.Comments = formValue.comments;
            this.application.currentBooking.IsInstantConfirm = this._instantConfirm;
            this.application.currentBooking.CreditCardType = this.selectedCardType.Id;
            this.application.currentBooking.CreditCardTypeName = this.autoCreditCardType.toString();
            this.application.currentBooking.MemberInfo = this.application.configuration.Member;
            this.application.currentBooking.BrandName = this.application.configuration.StoreName;
            this.application.currentBooking.ResortInventory = this.application.currentInventory;
            this.application.currentBooking.IsComplete = true;
            this.application.currentBooking.CreditCardType = this.autoCreditCardType;
            this.application.currentBooking.UpgradeCobrandId = this.application.currentInventory.upgradeCobrandId;
            this.application.currentBooking.UpgradePurchaseTypeId = this.application.currentInventory.upgradePurchaseTypeId;
            this.application.currentBooking.Guests = guests;
            this.application.currentBooking.Guests.forEach(g => g.DateOfBirth =
                new Date(g.DobYear, g.DobMonth - 1, g.DobDay)
            );
            
            if (this.states.length > 0) {
                let state: GeographicalRegion = this.states.first(s => s.Id === this.application.currentBooking.Address.RegionId);
                this.application.currentBooking.Address.RegionCode = state.Code;
                this.application.currentBooking.Address.Region = state.Name;
            } else {
                this.application.currentBooking.Address.RegionCode = this.application.currentBooking.Address.Region;
            }

            if (this.countries.length > 0) {
                let country: Country = this.countries.firstOrDefault(c => c.Id == this.application.currentBooking.Address.CountryId);

                if (country) {
                    this.application.currentBooking.Address.CountryCode = country.Code;
                    this.application.currentBooking.Address.Country = country.Name;
                } else {
                    this.mapApplicationInsightsObjects({ message: "Member country is missing or invalid" }, CheckoutPageEvents.CountryMissingOrInvalid);
                }
            }

            this.isCompleting = true;
            this.paymentDeclined = false;

            UserActivityLogger.logEvent(
                CheckoutPageEvents.CompleteBooking,
                {},
                UserActivityLoggerOptions.AppInsightsOnly
            );

            this._bookingService.Put(this.application.currentBooking, (r) => this.onCompleteBookingCompleted(r));
        }
        catch (ex) {
            if (!this.isBookingCompleted) {
                this.mapApplicationInsightsObjects(ex, CheckoutPageEvents.BookingFailure);
            } else {
                this.mapApplicationInsightsObjects(ex, CheckoutPageEvents.CheckoutPageError);
            }
        }
    }

    private mapApplicationInsightsObjects(ex, type) {
        const mapData = new Map();
        mapData.set("memberid", this.application.configuration.Member.Id);
        mapData.set("ovcpartner", this.application.configuration.Partner.LongName);
        mapData.set("ovcpartnerid", this.application.configuration.Partner.Id);
        mapData.set("memberclass", this.application.configuration.MemberClass);
        mapData.set("product", "Resort");
        mapData.set("errormessage", ex.message ? ex.message : "Checkout unknown exception");
        mapData.set("bookingerror", ex.message ? ex.message : "Checkout unknown exception");
        mapData.set("httperrorcode", ex.code ? ex.code : ex.status ? ex.status : '');
        mapData.set("origination", "ICE");

        let jsonObject = {};

        mapData.forEach((value, key) => {
            jsonObject[key] = value;
        });

        UserActivityLogger.logEvent(type, jsonObject, UserActivityLoggerOptions.AppInsightsOnly);
    }

    private onCompleteBookingCompleted(result: BookingResult): void {
        this.application.bookingCompletion = result;

        if (result.bookingComplete && String.isNullOrWhiteSpace(result.error)) {

            if (this.application.currentBooking.IsUpgradeMembershipSelected) {
                //set club cookie.
                try {
                    this.qryParamsService.applicationParams.cobrandId = this.application.currentBooking.UpgradeCobrandId.toString();
                    this.qryParamsService.store();
                    this.cookieService.setItem("club-reauth", "true", <CookieOptions>{ expires: 1, path: "/", domain: this.domainService.getRootDomain(), secure: this.domainService.isSsl() });
                } catch (e) {
                    console.log("unable to create club-reauth cookie" + e);
                }
            }

            this.application.currentBooking.numberOfAdults = (this.application.currentBooking.Guests !== null && this.application.currentBooking.Guests !== undefined) ? this.numberOfAdultPassengers() : undefined;
            this.application.currentBooking.numberOfChildren = (this.application.currentBooking.Guests !== null && this.application.currentBooking.Guests !== undefined) ? this.numberOfChildPassengers() : undefined;
            this.application.currentBooking.guestAges = this.agesOfPassenger();
            UserActivityLogger.publish(this.application.currentBooking, UserActivityPublishDataTypes.Booking);
            UserActivityLogger.logEvent(CheckoutPageEvents.BookingSuccessful, { bookingresult: JSON.stringify(result) }, UserActivityLoggerOptions.AppInsightsOnly);
            this.isBookingCompleted = true;
            this.router.navigate(['confirmation'], { queryParams: { sid: this.qryParamsService.encodedGuid } });
            return;
        }
        
            this.isCompleting = false;
            const mapData = new Map();
            mapData.set("memberid", this.application.configuration.Member.Id);
            mapData.set("ovcpartner", this.application.configuration.Partner.LongName);
            mapData.set("ovcpartnerid", this.application.configuration.Partner.Id);
            mapData.set("memberclass", this.application.configuration.MemberClass);
            mapData.set("product", "Resort");
            mapData.set("errormessage", result.error ? result.error : "Checkout unknown error");
            mapData.set("bookingerror", result.error ? result.error : "Checkout unknown error");
            mapData.set("origination", "ICE");
            mapData.set("bookingerrorjson", JSON.stringify(result));
            let jsonObject = {};
            mapData.forEach((value, key) => {
                jsonObject[key] = value;
            });

        if (!this.isBookingCompleted) {
            UserActivityLogger.logEvent(CheckoutPageEvents.BookingFailure, jsonObject, UserActivityLoggerOptions.AppInsightsOnly);
        } else {
            UserActivityLogger.logEvent(CheckoutPageEvents.CheckoutPageError, jsonObject, UserActivityLoggerOptions.AppInsightsOnly);
        }

        if (result.error === "Payment Declined.") {
            this.creditCardNumberRef.nativeElement.focus();
            this.paymentDeclined = true;;
            result.error = "Your credit card was declined. Please re-enter and try again.";
        }

    }

    public checkCreditCardType(value: string): void {
        this.autoCreditCardType = this.creditCardService.getCreditCardType(this.configuration.AcceptedCreditCardTypes, value);
        this.selectedCardType = this.configuration.AcceptedCreditCardTypes.where(e => e.Id === this.autoCreditCardType).singleOrDefault();
        this.application.currentBooking.CreditCardTypeName = this.selectedCardType ? this.selectedCardType.Name : "";
    }

    /**
     * Update purchaser info if Guest is same as purchaser
     * @param value Guest info is same as purchaser
     */
    public guestIsPurchaserChecked(value: boolean) {
        if (value) {
            this.assignGuestAsPurchaser(this.guestFormList.at(0).value);
        }
    }

    private assignGuestAsPurchaser(guest: ResortGuest): void {
        if (guest){
            const member = this.members.where(m => m.FirstName == guest.FirstName && m.LastName == guest.LastName).firstOrDefault();
            this.autoSelectMember(member);
        }
    }

    public guestMemberChanged(guest: ResortGuest): void {
        this.assignGuestAsPurchaser(guest);

        if (this.guestIsPurchaserChecked) {
            const creditCardName = `${guest.FirstName.trim()} ${guest.LastName.trim()}`;

            this.application.currentBooking.CreditCardName = creditCardName;
            this.application.currentBooking.FirstName = guest.FirstName.trim();
            this.application.currentBooking.LastName = guest.LastName.trim();
            this.showHideEdit = true;

            this.checkoutForm.get('creditCardName').setValue(creditCardName);
        }
    }

    /**
     * Log event when creditCardName changes
     */
    public onCreditCardNameChanged(): void {
        UserActivityLogger.logEvent(CheckoutPageEvents.CardNameUpdated, {}, UserActivityLoggerOptions.AppInsightsOnly);
    }

    public clickEdit(): void {
        this.showHideEdit = !this.showHideEdit;
        UserActivityLogger.logEvent(CheckoutPageEvents.BillingAddressEditButtonClicked, {}, UserActivityLoggerOptions.AppInsightsOnly);
    }

    public upgradeDialogSelectionChange(selection: boolean) {
        console.log("Upgrade Dialog Selection:" + selection);
        this.application.currentBooking.IsUpgradeMembershipSelected = selection;
        this.updateTotal++;
    }

    public numberOfAdultPassengers(): number {
        let adultCount: number = 0;
        this.application.currentBooking.Guests.forEach(g => {
            if (this.CalculateAge(g.DateOfBirth) >= 18) {
                adultCount = ++adultCount;
            }
        });
        return adultCount;
    }

    public numberOfChildPassengers(): number {
        let childCount: number = 0;
        this.application.currentBooking.Guests.forEach(g => {
            if (this.CalculateAge(g.DateOfBirth) < 18) {
                childCount = ++childCount;
            }
        });
        return childCount;
    }

    public agesOfPassenger(): string {

        let ages: string[] = [];
        this.application.currentBooking.Guests.forEach((g, index) => {
            if (this.CalculateAge(g.DateOfBirth) > 0) {
                ages[index] = this.CalculateAge(g.DateOfBirth).toString();
            }
        });
        return ages.join(',');
    }

    public CalculateAge(birthdate: Date): number {
        if (birthdate) {
            var timeDiff = Math.abs(Date.now() - birthdate.getTime());
            //Used Math.floor instead of Math.ceil
            //so 26 years and 140 days would be considered as 26, not 27.
            return Math.floor((timeDiff / (1000 * 3600 * 24)) / 365);
        }
        return 0;
    }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/check-out/check-out.component.ts